import { useCallback, useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useBolUpload, type BolUploadMode } from "@/hooks/useBolUpload";

interface BolUploaderProps {
  orderId: string;
  orderUniqueId: string;
  mode?: BolUploadMode;
  /** Render as a full dropzone card (initial-empty state) or a compact button (add-more state). */
  variant?: "dropzone" | "compact";
  onUploaded?: (uploaded: number) => void;
}

const ACCEPTED_TYPES =
  "image/jpeg,image/png,image/webp,image/gif,application/pdf";

export function BolUploader({
  orderId,
  orderUniqueId,
  mode = "dispatcher",
  variant = "dropzone",
  onUploaded,
}: BolUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const { uploadFiles, isUploading } = useBolUpload({
    orderId,
    orderUniqueId,
    mode,
  });

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const result = await uploadFiles(files);
      if (result.uploaded > 0) onUploaded?.(result.uploaded);
      if (inputRef.current) inputRef.current.value = "";
    },
    [uploadFiles, onUploaded],
  );

  if (variant === "compact") {
    return (
      <>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          className="hidden"
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={isUploading}
          onClick={() => inputRef.current?.click()}
          className="gap-1.5"
        >
          {isUploading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
          Add another BoL
        </Button>
      </>
    );
  }

  return (
    <Card
      className={cn(
        "border-2 border-dashed transition-colors cursor-pointer",
        dragOver
          ? "border-primary bg-primary/5"
          : "border-border hover:border-primary/50",
        isUploading && "pointer-events-none opacity-70",
      )}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
      }}
    >
      <CardContent className="flex flex-col items-center justify-center py-10 gap-3">
        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
          {isUploading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <Upload className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">
            {isUploading
              ? "Uploading…"
              : "Drop Bill of Lading files here or click to browse"}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            JPG, PNG, WEBP, PDF — multiple files allowed
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          className="hidden"
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
        />
      </CardContent>
    </Card>
  );
}
